const PERMISSION_LABELS = {
  GESTOR: 'Gestor',
  AVANCADO: 'Usuário Avançado',
  BASICO: 'Usuário Básico',
};

import React from 'react';
import styled from 'styled-components';
import { FiEdit, FiUser, FiMail, FiPhone, FiClock, FiShield } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const UserInfo = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const UserName = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const UserEmail = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${props => props.$ativo ? theme.colors.success[500] : theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ClientList = styled.div`
  margin-top: ${theme.spacing[3]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const ClientLabel = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${theme.spacing[2]};
`;

const ClientChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const ClientChip = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.primary[500]}20;
  color: ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getInitials = (name) => {
  if (!name) return 'U';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const getPermissaoLabel = (usuario) => {
  const codigo = usuario?.permissao?.codigo || usuario?.permissao_codigo;
  if (!codigo) {
    return 'Permissão padrão';
  }
  return PERMISSION_LABELS[codigo] || codigo;
};

const UsuarioCard = ({ usuario }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/usuarios/${usuario.uuid || usuario.id}/editar`);
  };

  return (
    <CardContainer>
      <Header>
        <UserAvatar>
          {getInitials(usuario.nome)}
        </UserAvatar>
        <UserInfo>
          <UserName>{usuario.nome}</UserName>
          <UserEmail>{usuario.email}</UserEmail>
        </UserInfo>
      </Header>

      <ContentSection>
        <Details>
          <DetailItem>
            <DetailIcon><FiShield /></DetailIcon>
            <span>{getPermissaoLabel(usuario)}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiPhone /></DetailIcon>
            <span>{usuario.telefone || 'Não informado'}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiClock /></DetailIcon>
            <span>Cadastrado em {formatDate(usuario.created_at)}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiShield /></DetailIcon>
            <StatusBadge $ativo={usuario.ativo}>
              {usuario.ativo ? 'Ativo' : 'Inativo'}
            </StatusBadge>
          </DetailItem>
        </Details>

        {usuario.clientes && usuario.clientes.length > 0 && (
          <ClientList>
            <ClientLabel>Clientes Associados</ClientLabel>
            <ClientChips>
              {usuario.clientes.map((cliente) => (
                <ClientChip key={cliente.id}>
                  {cliente.nome}
                </ClientChip>
              ))}
            </ClientChips>
          </ClientList>
        )}

        <Actions>
          <ActionButton $primary onClick={handleEdit}>
            <ActionIcon><FiEdit /></ActionIcon>
            Editar
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default UsuarioCard;
